import React, { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { api } from '../api';
import { fmt$, isIncomplete, refusalText, NAVY_GRADIENT } from './constants';
import { CalcSummaryTab, CalcDocsTab, CalcReasoningTab, CalcRawTab } from './CalcDetailTabs';
import { BRAND } from '../kit/tokens';
import { Sheet, SheetContent, SheetTitle } from '../../components/ui/sheet';
import { formatDateTime } from '../kit/time';

// ============================================================================
// CalcDetailDrawer — full audit view of a saved calculation
// Shows: summary, source documents, guideline reasoning (AI + citations),
// and raw inputs. Fetched from GET /api/income/{calc_id}.
// ============================================================================
export function CalcDetailDrawer({ calcId, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [tab, setTab] = useState('summary');
  const [error, setError] = useState(null);

  useEffect(() => {
    let stop = false;
    setBusy(true);
    setError(null);
    api.get(`/income/${calcId}`)
      .then(({ data }) => { if (!stop) setData(data); })
      .catch((e) => { if (!stop) setError(e.response?.data?.detail || 'Failed to load calculation'); })
      .finally(() => { if (!stop) setBusy(false); });
    return () => { stop = true; };
  }, [calcId]);

  const docs = data?.documents_used || [];
  const citations = data?.guideline_citations || [];
  const incomeTypeLabel = (() => {
    if (!data) return '';
    const it = data.input?.income_type;
    if (['w2_salary', 'w2_hourly', 'w2_overtime_bonus'].includes(it)) return 'W-2 Wage Earner';
    return (it || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  })();

  const tabs = [
    { k: 'summary', label: 'Summary' },
    { k: 'docs',    label: `Documents (${docs.length})` },
    { k: 'reason',  label: 'Guideline Reasoning' },
    { k: 'raw',     label: 'Raw Inputs' },
  ];

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        showClose={false}
        className="p-0 w-full max-w-2xl sm:max-w-2xl border-0 shadow-2xl overflow-y-auto bg-white"
        data-testid="calc-detail-drawer"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-bold text-brand-accent">Calculation Audit</div>
            <SheetTitle className="font-serif text-lg font-semibold text-brand-navy">
              {busy ? 'Loading…' : incomeTypeLabel}
            </SheetTitle>
            {data && (
              <div className="text-xs text-slate-500 mt-0.5">
                {formatDateTime(data.created_at)}
                {data.agency_guideline && <span> · <b className="uppercase">{data.agency_guideline}</b></span>}
                {data.borrower_name && <span> · {data.borrower_name}</span>}
              </div>
            )}
          </div>
          <button onClick={onClose} data-testid="calc-detail-close" className="p-1 hover:bg-slate-100 rounded-md">
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        {/* Result banner */}
        {data && (
          <div className="px-6 py-4" style={{ background: NAVY_GRADIENT }}>
            <div className="text-[10px] uppercase tracking-widest text-white/60">Qualifying Monthly Income</div>
            {isIncomplete(data) ? (
              <>
                <div className="font-serif text-4xl font-bold text-amber-300">Incomplete</div>
                <div className="text-xs text-amber-200 mt-1" data-testid="calc-detail-missing">
                  {refusalText(data)}
                </div>
              </>
            ) : (
              <>
                <div className="font-serif text-4xl font-bold text-brand-accent-soft">{fmt$(data.qualifying_monthly)}</div>
                <div className="text-xs text-white/70 mt-1">{data.method}</div>
              </>
            )}
          </div>
        )}

        {/* Tabs */}
        {data && (
          <div className="px-6 pt-3 border-b border-slate-100 flex gap-1 sticky top-[73px] bg-white">
            {tabs.map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`calc-detail-tab-${t.k}`}
                className="px-3 py-2 text-xs font-semibold rounded-t-md transition"
                style={tab === t.k
                  ? { color: BRAND.navy, borderBottom: `2px solid ${BRAND.accent}` }
                  : { color: '#64748b', borderBottom: '2px solid transparent' }}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="p-6">
          {busy && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
          {error && <div className="text-sm text-red-600">{error}</div>}
          {data && tab === 'summary' && <CalcSummaryTab data={data} />}
          {data && tab === 'docs' && <CalcDocsTab docs={docs} aggregate={data.aggregate_pay} />}
          {data && tab === 'reason' && <CalcReasoningTab data={data} citations={citations} />}
          {data && tab === 'raw' && <CalcRawTab data={data} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}
